import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Section, Field, errToast } from "@/components/tax/shared";
import { toast } from "sonner";

function ActionForm({ title, subtitle, fields, submitLabel, onSubmit, testId, danger }) {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onSubmit(v); setV({}); } catch (e) { errToast(e); } finally { setBusy(false); }
  };
  return (
    <Section title={title} subtitle={subtitle} testId={testId}>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((f) => f.type === "textarea" ? (
          <textarea key={f.key} data-testid={`${testId}-${f.key}`} rows={3} placeholder={f.label} value={v[f.key] || ""}
            onChange={(e) => setV({ ...v, [f.key]: e.target.value })} className="sm:col-span-2 rounded-md border border-input p-3 text-sm" />
        ) : f.type === "select" ? (
          <select key={f.key} data-testid={`${testId}-${f.key}`} value={v[f.key] || ""} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} className="h-10 rounded-md border border-input px-3 text-sm">
            <option value="">{f.label}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <Field key={f.key} id={`${testId}-${f.key}`} label={f.label} type={f.type || "text"} value={v[f.key] || ""} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} />
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <Button data-testid={`${testId}-submit`} disabled={busy} onClick={go} className={danger ? "bg-slate-900 hover:bg-slate-800 text-white rounded-full" : "bg-red-600 hover:bg-red-700 text-white rounded-full"}>{submitLabel}</Button>
      </div>
    </Section>
  );
}

export function PreparerActions({ c, refresh }) {
  const post = (path, body, msg) => api.post(`/preparer/cases/${c.id}/${path}`, body).then(() => { toast.success(msg); refresh(); });
  const t183ok = c.t183?.final && !c.t183?.superseded;
  return (
    <div className="space-y-5" data-testid="preparer-actions">
      <ActionForm testId="prep-review" title="Review status & customer update" submitLabel="Update"
        fields={[{ key: "review_status", label: "Review status", type: "select", options: [
          { value: "not_started", label: "Not started" }, { value: "in_review", label: "In review" },
          { value: "waiting_on_client", label: "Waiting on client" }, { value: "ready_to_file", label: "Ready to file" }] },
          { key: "note", label: "Optional message to the customer", type: "textarea" }]}
        onSubmit={(v) => post("review-status", { review_status: v.review_status || c.review_status || "in_review", note: v.note }, "Review status updated")} />
      {!["filed", "accepted"].includes(c.status) && (
        <ActionForm testId="prep-request-info" title="Request missing information" subtitle="Sets the case to Action Required and notifies the customer." submitLabel="Send request"
          fields={[{ key: "message", label: "What do you need? (e.g. upload your T5 from Bank X)", type: "textarea" }]}
          onSubmit={(v) => post("request-info", { message: v.message || "" }, "Request sent")} />
      )}
      {["sent_to_preparer", "rejected"].includes(c.status) && !t183ok && (
        <ActionForm testId="prep-t183" title="Finalize & request T183 signature" subtitle="Enter the final figures from your CRA-certified software. The customer must sign T183 before you file." submitLabel="Request T183 signature"
          fields={[{ key: "final_refund", label: "Final refund ($)", type: "number" }, { key: "final_balance_owing", label: "Final balance owing ($)", type: "number" },
            { key: "software", label: "Certified software used (e.g. TaxCycle)" }, { key: "note", label: "Note to customer (optional)", type: "textarea" }]}
          onSubmit={(v) => post("request-t183", { final_refund: Number(v.final_refund || 0), final_balance_owing: Number(v.final_balance_owing || 0), software: v.software || "", note: v.note }, "T183 requested")} />
      )}
      {c.status === "sent_to_preparer" && t183ok && (
        <ActionForm testId="prep-filed" title="Record EFILE confirmation" subtitle="Only after you have actually transmitted the return and received CRA's confirmation number." submitLabel="Record filing"
          fields={[{ key: "filed_date", label: "Filing date", type: "date" }, { key: "confirmation_number", label: "CRA EFILE confirmation number" },
            { key: "software", label: "Software used" }, { key: "note", label: "Note (optional)", type: "textarea" }]}
          onSubmit={(v) => post("filed", { filed_date: v.filed_date || "", confirmation_number: v.confirmation_number || "", software: v.software || "", note: v.note }, "Filing recorded")} />
      )}
      {c.status === "filed" && (
        <ActionForm testId="prep-outcome" title="Record CRA outcome" submitLabel="Record outcome" danger
          fields={[{ key: "outcome", label: "Outcome", type: "select", options: [{ value: "accepted", label: "Accepted" }, { value: "rejected", label: "Rejected" }] },
            { key: "date", label: "Date", type: "date" }, { key: "reference", label: "Reference (optional)" }, { key: "reason", label: "Rejection reason / error code", type: "textarea" }]}
          onSubmit={(v) => post("outcome", { outcome: v.outcome || "", date: v.date || "", reference: v.reference, reason: v.reason }, "Outcome recorded")} />
      )}
    </div>
  );
}
